// src/components/AddressInput.js
import React, { useState, useEffect } from 'react';

function AddressInput({ onLocationSelect, required = true, initialValue = '' }) {
  const [isUsingCurrentLocation, setIsUsingCurrentLocation] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [addressInput, setAddressInput] = useState(initialValue);
  const [suggestions, setSuggestions] = useState([]);
  const [isLoadingAPI, setIsLoadingAPI] = useState(true);
  const [googleMapsReady, setGoogleMapsReady] = useState(false);

  // Load Google Maps API dynamically
  useEffect(() => {
    const loadGoogleMapsAPI = () => {
      // Check if already loaded
      if (window.google && window.google.maps && window.google.maps.places) {
        setGoogleMapsReady(true);
        setIsLoadingAPI(false);
        return;
      }

      // Create script tag
      const script = document.createElement('script');
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

      if (!apiKey) {
        setLocationError('Google Maps API key not configured');
        setIsLoadingAPI(false);
        return;
      }

      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
      script.async = true;
      script.defer = true;

      script.onload = () => {
        setGoogleMapsReady(true);
        setIsLoadingAPI(false);
      };

      script.onerror = () => {
        setLocationError('Failed to load Google Maps API');
        setIsLoadingAPI(false);
      };

      document.head.appendChild(script);

      // Global handler for Google Maps Auth Failures
      window.gm_authFailure = () => {
        setLocationError('Google Maps API Error: Authentication failed. Please check your API key restrictions.');
        setIsLoadingAPI(false);
      };
    };

    loadGoogleMapsAPI();

    // Cleanup
    return () => {
      window.gm_authFailure = null;
    };
  }, []);

  // Initialize Places Autocomplete when API is ready
  useEffect(() => {
    if (!googleMapsReady || !addressInput.trim()) {
      setSuggestions([]);
      return;
    }

    const timeoutId = setTimeout(() => {
      const service = new window.google.maps.places.AutocompleteService();

      service.getPlacePredictions(
        {
          input: addressInput,
          componentRestrictions: { country: 'us' },
        },
        (predictions, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
            setSuggestions(predictions.slice(0, 5)); // Limit to 5 suggestions
          } else {
            setSuggestions([]);
          }
        }
      );
    }, 300); // Debounce

    return () => clearTimeout(timeoutId);
  }, [addressInput, googleMapsReady]);

  const handleSuggestionSelect = async (suggestion) => {
    setAddressInput(suggestion.description);
    setSuggestions([]);
    setLocationError('');

    if (!googleMapsReady) {
      setLocationError('Google Maps API not ready');
      return;
    }

    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode({ placeId: suggestion.place_id }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const location = results[0].geometry.location;
        const locationData = {
          address: suggestion.description,
          coordinates: `${location.lat()},${location.lng()}`,
          latitude: location.lat(),
          longitude: location.lng()
        };
        onLocationSelect(locationData);
      } else {
        setLocationError('Failed to resolve location. Please try another address.');
      }
    });
  };

  const handleCurrentLocation = () => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by this browser.');
      return;
    }

    setIsUsingCurrentLocation(true);
    setLocationError('');

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;

        // Reverse geocode if Google Maps is ready
        if (googleMapsReady) {
          const geocoder = new window.google.maps.Geocoder();
          const latLng = new window.google.maps.LatLng(latitude, longitude);

          geocoder.geocode({ location: latLng }, (results, status) => {
            let address = 'Current Location';
            if (status === 'OK' && results[0]) {
              address = results[0].formatted_address;
            }

            const locationData = {
              address,
              coordinates: `${latitude},${longitude}`,
              latitude,
              longitude
            };

            setAddressInput(address);
            onLocationSelect(locationData);
            setIsUsingCurrentLocation(false);
          });
        } else {
          // Fallback without reverse geocoding
          const locationData = {
            address: 'Current Location',
            coordinates: `${latitude},${longitude}`,
            latitude,
            longitude
          };

          setAddressInput('Current Location');
          onLocationSelect(locationData);
          setIsUsingCurrentLocation(false);
        }
      },
      (error) => {
        console.error('Geolocation error:', error);
        let errorMessage = 'Failed to get your location.';

        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location access was denied. Please enable location services.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information is unavailable.';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out.';
            break;
        }

        setLocationError(errorMessage);
        setIsUsingCurrentLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000
      }
    );
  };

  const handleManualSubmit = async () => {
    if (!addressInput.trim()) {
      setLocationError('Please enter an address.');
      return;
    }

    if (!googleMapsReady) {
      // Fallback: just use the text as-is
      const locationData = {
        address: addressInput,
        coordinates: null,
        latitude: null,
        longitude: null
      };
      onLocationSelect(locationData);
      return;
    }

    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode({ address: addressInput }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const location = results[0].geometry.location;
        const locationData = {
          address: results[0].formatted_address,
          coordinates: `${location.lat()},${location.lng()}`,
          latitude: location.lat(),
          longitude: location.lng()
        };
        onLocationSelect(locationData);
      } else {
        setLocationError('Could not find that address. Please try a different one.');
      }
    });
  };

  return (
    <div className="space-y-3">
      {/* Address Input Field */}
      <div className="relative">
        <input
          type="text"
          value={addressInput}
          onChange={(e) => setAddressInput(e.target.value)}
          placeholder={isLoadingAPI ? "Loading address search..." : "Start typing an address..."}
          className={`w-full border rounded-md px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${locationError ? 'border-red-300' : 'border-gray-300'
            }`}
          required={required}
          disabled={isLoadingAPI}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (suggestions.length === 0) {
                handleManualSubmit();
              }
            }
          }}
        />

        {/* Loading indicator */}
        {isLoadingAPI && (
          <div className="absolute right-3 top-3">
            <div className="animate-spin h-4 w-4 border-2 border-gray-300 border-t-indigo-600 rounded-full"></div>
          </div>
        )}
      </div>

      {/* Suggestions Dropdown */}
      {suggestions.length > 0 && (
        <div className="border border-gray-200 rounded-md bg-white shadow-lg max-h-48 overflow-y-auto z-10">
          {suggestions.map((suggestion) => (
            <div
              key={suggestion.place_id}
              onClick={() => handleSuggestionSelect(suggestion)}
              className="cursor-pointer px-3 py-2 hover:bg-gray-100 text-sm border-b border-gray-100 last:border-b-0"
            >
              {suggestion.description}
            </div>
          ))}
        </div>
      )}

      {/* Error Message */}
      {locationError && (
        <p className="text-red-500 text-sm">{locationError}</p>
      )}

      {/* Current Location Button */}
      <div className="flex justify-center">
        <button
          type="button"
          onClick={handleCurrentLocation}
          disabled={isUsingCurrentLocation}
          className={`px-4 py-2 text-sm rounded-md border transition-colors ${isUsingCurrentLocation
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
            : 'bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100'
            }`}
        >
          {isUsingCurrentLocation ? (
            <span className="flex items-center">
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Getting location...
            </span>
          ) : (
            'Use Current Location'
          )}
        </button>
      </div>

      {/* Manual Submit Button */}
      {addressInput && suggestions.length === 0 && googleMapsReady && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={handleManualSubmit}
            className="px-4 py-2 text-sm bg-indigo-50 text-indigo-700 rounded-md border border-indigo-200 hover:bg-indigo-100"
          >
            Use "{addressInput}"
          </button>
        </div>
      )}
    </div>
  );
}

export default AddressInput;